import { track, trackBeacon, type CoreEventMap, type FunnelEventMap, type ToolEventMap } from '@core/utils/analytics'

// NarcisEventMap = the narcis_funnel event dictionary (FunnelEventMap, @core/utils/analytics.ts)
// plus narcis-only additions: swap/portrait/officina-tools features ovid doesn't ship, and cross-
// cutting infra (gate_hit, face_slots_restored) shared with the untouched portrait/swap surfaces.
//
// `FunnelEventMap` REPLACES the old `PackModelEventMap` here (narcis_funnel plan, Architecture
// decision 8: "clean slate scoped to funnel surfaces" — pack pages, hub, /model, auth, My Packs,
// emails speak only the dictionary now; `tool_*` and portrait telemetry are untouched, which is
// why gate_hit/face_slots_restored are re-declared directly below rather than dropped — both are
// shared with the portrait surface's own `narcisTrack` calls, which this step does not touch).
// `sign_up` is omitted from the intersection: it doesn't fire through `narcisTrack` at all (see
// FunnelEventMap's own header comment for where it does).
export type NarcisEventMap = Omit<FunnelEventMap, 'sign_up'> & ToolEventMap & {
    // Restored 2026-08-01 (plan 2026_08_01_ga4_wiring_defect_remediation.md, D4) — dropped under
    // decision 8 in the funnel cutover; ovid kept its own declaration (OvidEventMap) the whole
    // time. Same closed reason set as the start-training action can actually return
    // (`_layout.model.tsx`) — see ModelIntake.tsx's REFUSAL_REASONS.
    model_training_refused: { reason: 'already-training' | 'invalid-photo-count' | 'consent-required' | 'photos-unavailable' | 'payment-required' }
    // Pay at launch (plan § 7): the model checkout opened. GA4's own `value`/`currency` only —
    // the core shape's `price_id` is archived on the narcis property.
    checkout_started: { value: number; currency: string }
    // GA4's standard purchase, fired once when the paid Start is confirmed; `transaction_id` is the
    // Checkout Session id.
    purchase: { transaction_id: string; value: number; currency: string }
    checkout_cancelled: Record<string, never>
    // The path through the wall (plan 2026_11_04_narcis_wall_path.md): the intake page's Continue, with the photos it
    // carries on; and the feature page's arrival from that path (not a direct visit, not the mail's link).
    model_intake_continue: { photo_count: number }
    // The intake page seen, once per arrival; `from` is the way in (`path.ts` `intakeFrom`).
    model_intake_seen: { from: 'wall' | 'retrain' | 'header' | 'pack' | 'pricing' | 'issue' | 'direct' }
    // The intake's file picker opened: each press on its drop area, its empty slots or its button.
    intake_picker_opened: Record<string, never>
    model_proof_seen: Record<string, never>
    face_rejected_compliance: Record<string, never>
    face_removed: { slot: number; face_count: number }
    face_drop_rejected: { reason: 'cap_reached' | 'not_image' | 'unsupported_format' }
    generation_dropped_no_face: { trigger: 'submitResponse' | 'recompose' | 'triggerGeneration' }
    tos_accepted: { mode: 'generate' | 'swap' | 'portrait' }
    // Auth gate hit — shared cross-cutting infra (portrait's paywall gate + pack pages' signup
    // gate both fire it; portrait telemetry is untouched by the funnel dictionary, so this stays
    // declared directly rather than dropping with the rest of the old PackModelEventMap taxonomy).
    gate_hit: { gate: string; trigger: string; packSlug?: string }
    // Face slots restored from sessionStorage/global stash — shared with portrait
    // (useFaceSlots.ts), same rationale as gate_hit above.
    face_slots_restored: { slot_count: number; restore_source: 'portrait' | 'global' }
    // /model entry attribution. The header dropdown's 'userblock_dropdown' was never touched by
    // the funnel cutover. 'pack_gate' — the pack page's
    // train-CTA — was dropped at the cutover on the theory that no dictionary event covered the
    // moment; RESTORED 2026-08-11 (DEVIATIONS.md #106): fired from the stage's primary on the ad
    // and no_model stages, which opens the model intake with nothing downstream to report the tap.
    // 'your_model_block' stays
    // deliberately unfired here — see that component's own call site, still decision-8'd for a
    // surface this pass didn't touch.
    model_cta_click: { source: 'your_model_block' | 'userblock_dropdown' | 'pack_gate' }
    // Homepage hero CTA — homepage is not one of the six named funnel surfaces (pack pages, hub,
    // /model, auth, My Packs, emails), so AnimatedWall.tsx's call sites are untouched.
    home_hero_cta_click: { destination: string; label: string; tier: 'primary' | 'ghost' | 'text' }
    // Client-side image compression failure — a technical diagnostic shared across every surface
    // that compresses an upload (tools, portrait, /model intake, pack pages), not funnel-specific
    // taxonomy; same cross-cutting rationale as gate_hit/face_slots_restored above.
    upload_compress_failed: CoreEventMap['upload_compress_failed']
    // Every pack card click on the hub grid and the homepage grid. position is 1-indexed within
    // its surface's own grid (hub grid or homepage grid), not a global rank. Kept declared for the
    // homepage grid's call site (`_layout._index.tsx`, not one of the six named funnel surfaces);
    // the hub's own call site (`_layout.packs.tsx`, in scope) is removed — see that file.
    pack_card_clicked: { packSlug: string; surface: 'hub' | 'home'; category: string; position: number }
    // Global nav click — fires on every header nav link tap. Narcis-only (ovid has no equivalent
    // instrumented yet).
    nav_click: { destination: string; from_page: string; label: string }
    // Portrait funnel events (Theme L, wave 2D). Narcis-only: ovid has no portrait surface.
    // portrait_first_action: fires on the first deliberate action from state 0 (read_me tap).
    portrait_first_action: { action: 'read_me' }
    // portrait_keep_rate: fires when the user keeps a take; slot is 0|1|2, batch_done_count
    // is how many takes in the batch reached 'done' (≥1, normally 2 per batch=2 config).
    portrait_keep_rate: { slot: 0 | 1 | 2; batch_done_count: number }
    // portrait_keep_failed: fires when the keep fetch fails for any reason. reason taxonomy:
    //   'network'  — fetch() rejected (offline, timeout, DNS)
    //   'http_4xx' — server returned 4xx (400 invalid request, 401 session expired)
    //   'http_5xx' — server returned 5xx (503 DB persist failure)
    //   'abort'    — AbortController.abort() fired (fork, back-to-draft, unmount)
    portrait_keep_failed: { reason: 'network' | 'http_4xx' | 'http_5xx' | 'abort' }
    // portrait_soft_cap_crossing: fires once per session-mount when monthlyRenderCount >= 90.
    portrait_soft_cap_crossing: { render_count: number }
    // G4–G9: portrait render-batch lifecycle and deliberate user actions. (GA audit #9)
    // G4: render batch started (one per useRenderBatch.start invocation).
    // from_model: true when the batch renders from the owner's trained model (zero uploaded
    // faces + a ready model — the same condition the render path itself uses to source the
    // face, see useRenderBatch.ts's RenderBatchOptions.fromModel).
    portrait_render_started: { stream_count: 1 | 2 | 3; trigger: 'render' | 'again' | 'render_one' | 'retry' | 'fork'; prompt_fallback: boolean; from_model: boolean }
    // G5: all streams in the batch reached done.
    portrait_render_completed: { stream_count: 1 | 2 | 3; n_done: number; from_model: boolean }
    // G6: batch reached error state (user charged, nothing rendered).
    portrait_render_failed: { stream_count: 1 | 2 | 3; error_type: 'face_not_detected' | 'studio_required' | 'batch_start' | 'stream'; from_model: boolean }
    // G9: user explicitly requests a new sentence.
    portrait_sentence_rerolled: { trigger: 'reroll' | 'reread' }
    // Global stash lifecycle events (plan 2026-05-25). Narcis-only: ovid's face slots don't
    // persist to a cross-tab global stash.
    // face_global_stash_written fires on every successful write to STORAGE_KEY.
    // slot_count is the number of non-null slots after the write; trigger distinguishes
    // a slot upload from a per-slot delete (remove writes a tombstone, not a slot).
    face_global_stash_written: { slot_count: number; trigger: 'set' | 'remove' }
    // face_global_stash_failed fires when the persistence layer encounters a terminal error.
    // reason: 'quota' = QuotaExceededError after retry; 'unavailable' = any other throw;
    // 'parse' = JSON parse failure or version mismatch on read.
    face_global_stash_failed: { reason: 'quota' | 'unavailable' | 'parse' }
}

// Straight passthrough to the shared core track() — narcisTrack exists to carry the
// NarcisEventMap typing, not to add behavior. browser_session_id is no longer merged
// here: that bridge now lives in @core/utils/analytics's setBrowserSessionId, called
// once from the shared app/root.tsx and carried as a GA4 user property alongside db_user_id,
// covering narcis, quies, and ovid uniformly instead of a per-product copy.
export const narcisTrack = <E extends keyof NarcisEventMap>(
    event: E, params: NarcisEventMap[E]
) => track(event as any, params as any)

// `narcisTrack` for a press that leaves the page (`trackBeacon`, @core/utils/analytics.ts).
export const narcisTrackBeacon = <E extends keyof NarcisEventMap>(
    event: E, params: NarcisEventMap[E]
) => trackBeacon(event as any, params as any)
